import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Smartphone, ShieldCheck, Loader2, BookOpen, Globe } from 'lucide-react';
import { useUserStore } from './store/useUserStore';
import { Logo } from './components/Logo';
import { Analytics } from './components/Analytics';
import { ScrollToTop } from './components/ScrollToTop';
import SalesPage from './SalesPage';
import { ThemeProvider } from './components/ThemeProvider';

// Lazy load the main applications to drastically improve initial page load speed
const MobileApp = lazy(() => import('./MobileApp'));
const AdminPortal = lazy(() => import('./admin/AdminPortal'));
const KnowledgeBase = lazy(() => import('./KnowledgeBase'));
const LegalModal = lazy(() => import('./components/LegalModal'));
const TermsOfService = lazy(() => import('./TermsOfService'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const ScienceResearch = lazy(() => import('./ScienceResearch'));
const SoundTherapyGuide = lazy(() => import('./SoundTherapyGuide'));
const CorporatePage = lazy(() => import('./pages/CorporatePage'));
const HRDashboard = lazy(() => import('./pages/HRDashboard'));
const SEOBinauralBeats = lazy(() => import('./pages/SEOBinauralBeats'));
const SEOFocusFrequencies = lazy(() => import('./pages/SEOFocusFrequencies'));
const SEOBrainwaveEntrainment = lazy(() => import('./pages/SEOBrainwaveEntrainment'));
const SEOAutismSensory = lazy(() => import('./pages/SEOAutismSensory'));
const SEOVestibular = lazy(() => import('./pages/SEOVestibular'));
const SEOTinnitus = lazy(() => import('./pages/SEOTinnitus'));
const SEOADAGuide = lazy(() => import('./pages/SEOADAGuide'));
const SEOBinauralVsIsochronic = lazy(() => import('./pages/SEOBinauralVsIsochronic'));
const SEOBrainwaveStatesFAQ = lazy(() => import('./pages/SEOBrainwaveStatesFAQ'));
const AboutPage = lazy(() => import('./pages/AboutPage'));

const LoadingFallback = () => (
  <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center text-zinc-500 relative overflow-hidden">
    {/* Subtle background glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-emerald-900/10 rounded-full blur-[100px] pointer-events-none mix-blend-screen animate-pulse"></div>
    
    <div className="relative z-10 flex flex-col items-center animate-in fade-in zoom-in-95 duration-500">
      <div className="relative mb-8">
        <div className="w-20 h-20 bg-emerald-500/10 rounded-3xl flex items-center justify-center border border-emerald-500/20 backdrop-blur-sm shadow-[0_0_30px_rgba(16,185,129,0.15)]">
          <Logo className="w-10 h-10 text-emerald-400" aria-hidden="true" />
        </div>
      </div>
      
      <div className="flex flex-col items-center gap-3">
        <h2 className="text-2xl font-light text-white tracking-tight">Neuros Box</h2>
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-500/80">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          <span>Initializing workspace...</span>
        </div>
      </div>
    </div>
  </div>
);

export default function App() {
  const [showLegal, setShowLegal] = useState<'tos' | 'privacy' | null>(null);
  const { ambiance } = useUserStore();

  React.useEffect(() => {
    import('./utils/webMcp').then(({ initWebMcp }) => initWebMcp());
  }, []);

  // Inject a global class to override motion properties for standard CSS 
  React.useEffect(() => {
    if (ambiance.vestibularSafeMode) {
      document.body.classList.add('vestibular-safe');
    } else {
      document.body.classList.remove('vestibular-safe');
    }

    if (ambiance.lowStimulationUI) {
      document.body.classList.add('low-stim-ui');
    } else {
      document.body.classList.remove('low-stim-ui');
    }
  }, [ambiance.vestibularSafeMode, ambiance.lowStimulationUI]);

  return (
    <MotionConfig reducedMotion={ambiance.seizureSafeMode || ambiance.vestibularSafeMode ? "always" : "user"}>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <a 
            href="#main-content" 
            className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-emerald-500 focus:text-black focus:font-semibold focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:shadow-2xl focus:w-auto focus:h-auto focus:overflow-visible focus:clip-auto"
          >
            Skip to main content
          </a>
          <Analytics />
          <Routes>
            <Route path="/" element={<SalesPage onExit={() => window.location.href = '/app'} />} />
            <Route path="/app" element={
              <Suspense fallback={<LoadingFallback />}>
                <MobileApp onExit={() => window.location.href = '/'} />
              </Suspense>
            } />
            <Route path="/admin" element={
              <Suspense fallback={<LoadingFallback />}>
                <AdminPortal onExit={() => window.location.href = '/'} />
              </Suspense>
            } />
            <Route path="/learn" element={
              <Suspense fallback={<LoadingFallback />}>
                <KnowledgeBase onExit={() => window.location.href = '/'} />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<LoadingFallback />}>
                <TermsOfService />
              </Suspense>
            } />
            <Route path="/privacy" element={
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </Suspense>
            } />
            <Route path="/science" element={
              <Suspense fallback={<LoadingFallback />}>
                <ScienceResearch />
              </Suspense>
            } />
            <Route path="/about" element={
              <Suspense fallback={<LoadingFallback />}>
                <AboutPage />
              </Suspense>
            } />
            <Route path="/guide" element={
              <Suspense fallback={<LoadingFallback />}>
                <SoundTherapyGuide />
              </Suspense>
            } />
            <Route path="/corporate" element={
              <Suspense fallback={<LoadingFallback />}>
                <CorporatePage />
              </Suspense>
            } />
            <Route path="/hr-dashboard" element={
              <Suspense fallback={<LoadingFallback />}>
                <HRDashboard />
              </Suspense>
            } />
            <Route path="/do-binaural-beats-work" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOBinauralBeats />
              </Suspense>
            } />
            <Route path="/best-frequencies-focus" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOFocusFrequencies />
              </Suspense>
            } />
            <Route path="/brainwave-entrainment-guide" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOBrainwaveEntrainment />
              </Suspense>
            } />
            <Route path="/autism-sensory-friendly-sound-therapy" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOAutismSensory />
              </Suspense>
            } />
            <Route path="/vestibular-safe-audio-motion" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOVestibular />
              </Suspense>
            } />
            <Route path="/tinnitus-relief-binaural-beats" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOTinnitus />
              </Suspense>
            } />
            <Route path="/getting-started-ada-audio-tools" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOADAGuide />
              </Suspense>
            } />
            <Route path="/binaural-beats-vs-isochronic-tones" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOBinauralVsIsochronic />
              </Suspense>
            } />
            <Route path="/brainwave-frequencies-faq" element={
              <Suspense fallback={<LoadingFallback />}>
                <SEOBrainwaveStatesFAQ />
              </Suspense>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {showLegal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="relative w-full max-w-2xl h-[80vh] bg-zinc-950 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" aria-hidden="true" /></div>}>
                  <LegalModal type={showLegal} onClose={() => setShowLegal(null)} />
                </Suspense>
              </div>
            </div>
          )}
        </BrowserRouter>
      </ThemeProvider>
    </MotionConfig>
  );
}
